import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { TextField } from "../ui/components/TextField";
import { Select } from "../ui/components/Select";
import { Loader } from "../ui/components/Loader";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { FeatherArrowLeft } from "@subframe/core";
import { FeatherAlertTriangle } from "@subframe/core";
import { FeatherCalendar } from "@subframe/core";
import { FeatherPlus } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherChevronDown } from "@subframe/core";
import { FeatherBuilding } from "@subframe/core";

import {
  journalsAPI,
  CreateJournalLineItem,
  GLMap,
  fetchCurrencies,
  Currency,
  UpdateTemplateRequest,
} from "../services/api";
import { LocationPicker } from "../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../shared/hooks/useActiveLocations";
import { getAllCustomers } from "../services/customerApi";
import { getAllVendors } from "../services/vendorApi";
import ConfirmEditRecurringJournalModal, {
  TemplateChange,
} from "../components/ConfirmEditRecurringJournalModal";
import {
  SearchableDropdown,
  GroupedItems,
  ErrorBanner,
  useToast,
} from "../shared/presentation/components";
import { useErrorHandling } from "../shared/presentation/hooks";

interface TemplateLineItemForm {
  id: string;
  account_type: string;
  account_id: string;
  account_name: string;
  description: string;
  contact_id: string;
  contact_name: string;
  customer_or_vendor: "customer" | "vendor" | "";
  debit: string;
  credit: string;
}

const EditRecurringJournalPage: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();

  // Form state
  const [templateName, setTemplateName] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  // ZOUR-65 F4: the GST registration every journal this template mints is filed
  // under.
  const [locationId, setLocationId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [reportingMethod, setReportingMethod] = useState<string>("both");

  // Recurring journal state
  const [recurringFrequency, setRecurringFrequency] = useState("");
  const [recurringInterval, setRecurringInterval] = useState<number | "">("");
  const [recurringStartDate, setRecurringStartDate] = useState("");
  const [recurringEndDate, setRecurringEndDate] = useState("");
  const [recurringNeverExpires, setRecurringNeverExpires] = useState(false);
  const [referenceNumberPrefix, setReferenceNumberPrefix] = useState("");

  // Line items state
  const [lineItems, setLineItems] = useState<TemplateLineItemForm[]>([]);

  // Dropdown options state
  const [accountTypes, setAccountTypes] = useState<string[]>([]);
  const [accountsByType, setAccountsByType] = useState<Record<string, GLMap[]>>(
    {},
  );
  const [customers, setCustomers] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
  } = useActiveLocations();

  const [currencies, setCurrencies] = useState<Currency[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [accountTypesLoading, setAccountTypesLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);

  // Error handling with new hook
  const {
    errors,
    setFieldError,
    setFieldErrors,
    setApiErrors,
    clearFieldError,
    hasLineItemError,
  } = useErrorHandling();

  // Toast notifications
  const { showSuccess, showError } = useToast();

  const [pageError, setPageError] = useState<string | null>(null);

  // Confirm edit modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Track original values for change detection
  const [originalData, setOriginalData] = useState<{
    templateName: string;
    currencyId: string;
    // ZOUR-65 F4: without this, changing ONLY the location leaves
    // `detectChanges()` empty and the Update button disabled.
    locationId: string | null;
    notes: string;
    reportingMethod: string;
    recurringFrequency: string;
    recurringInterval: number | "";
    recurringStartDate: string;
    recurringEndDate: string;
    recurringNeverExpires: boolean;
    referenceNumberPrefix: string;
    lineItems: TemplateLineItemForm[];
  } | null>(null);

  const [originalDataFinalized, setOriginalDataFinalized] = useState(false);

  // Helper function to format account type display
  const formatAccountType = (accountType: string): string => {
    return accountType.replace(/_/g, " ").toUpperCase();
  };

  // Helper function to determine if account requires vendor
  const shouldShowOnlyVendors = (accountName: string): boolean => {
    return (
      accountName.toLowerCase().includes("accounts payable") ||
      accountName.toLowerCase().includes("account payable")
    );
  };

  // Helper function to determine if account requires customer
  const shouldShowOnlyCustomers = (accountName: string): boolean => {
    return (
      accountName.toLowerCase().includes("accounts receivable") ||
      accountName.toLowerCase().includes("account receivable")
    );
  };

  // Helper function to check if contact is required
  const isContactRequired = (accountName: string): boolean => {
    return (
      shouldShowOnlyVendors(accountName) || shouldShowOnlyCustomers(accountName)
    );
  };

  // Helper function to check if contact currency matches journal currency
  const isContactCurrencyMatching = (
    contact: any,
    isVendor: boolean,
  ): boolean => {
    if (!currencyId) return true;

    if (isVendor) {
      return contact.currency_id === currencyId;
    } else {
      return contact.currency_code === currencyId;
    }
  };

  // Helper function to build grouped contacts for a line item
  const buildGroupedContacts = (accountName: string): GroupedItems<any>[] => {
    const groups: GroupedItems<any>[] = [];

    const showOnlyVendors = shouldShowOnlyVendors(accountName);
    const showOnlyCustomers = shouldShowOnlyCustomers(accountName);

    // Add customers group if applicable
    if (!showOnlyVendors && customers.length > 0) {
      const filteredCustomers = customers.filter((customer) =>
        isContactCurrencyMatching(customer, false),
      );
      if (filteredCustomers.length > 0) {
        groups.push({
          groupLabel: "Customers",
          items: filteredCustomers,
        });
      }
    }

    // Add vendors group if applicable
    if (!showOnlyCustomers && vendors.length > 0) {
      const filteredVendors = vendors.filter((vendor) =>
        isContactCurrencyMatching(vendor, true),
      );
      if (filteredVendors.length > 0) {
        groups.push({
          groupLabel: "Vendors",
          items: filteredVendors,
        });
      }
    }

    return groups;
  };

  // Helper to get contact display name
  const getContactDisplayName = (contactId: string, item: any): string => {
    if (!contactId) return "Select contact";

    // Check customers
    const customer = customers.find((c) => c.zoho_customerid === contactId);
    if (customer) return customer.customer_name;

    // Check vendors
    const vendor = vendors.find((v) => v.zoho_vendorid === contactId);
    if (vendor) return vendor.vendor_legal_name;

    return "Select contact";
  };

  // Load template data
  useEffect(() => {
    const loadTemplateData = async () => {
      if (!templateId) {
        setPageError("No template ID provided");
        setIsPageLoading(false);
        return;
      }

      try {
        setIsPageLoading(true);
        const response = await journalsAPI.getJournalTemplateById(templateId);
        const template = response.data;

        // Populate form fields
        setTemplateName(template.template_name);
        setCurrencyId(template.currency_id || "");
        setCurrencySymbol(template.currency_symbol || "₹");
        setNotes(template.notes || "");
        setReportingMethod(template.type || "both");
        setRecurringFrequency(template.recurrence_frequency);
        setRecurringInterval(template.recurrence_interval || "");
        setRecurringStartDate(template.starts_on);
        setRecurringEndDate(template.ends_on || "");
        // Convert never_expires to boolean (can be string "1", number 1, or boolean true)
        const neverExpires =
          template.never_expires === "1" ||
          template.never_expires === 1 ||
          template.never_expires === true;
        setRecurringNeverExpires(neverExpires);
        setReferenceNumberPrefix(template.reference_number_prefix || "");
        // ZOUR-65 F4: NULL means the primary location, so it is carried through
        // as-is rather than coerced to an empty string.
        setLocationId(template.location_id ?? null);

        // Convert line items to form format - we'll determine account_type later
        const formLineItems: TemplateLineItemForm[] = template.line_items
          ? template.line_items.map((item, index) => ({
              id: item.id ? item.id.toString() : (index + 1).toString(),
              account_type: "", // Will be populated after we load all account types
              account_id: item.account_id.toString(),
              account_name: item.account_name,
              description: item.description,
              contact_id: item.contact_id || "",
              contact_name: item.contact_name || "",
              customer_or_vendor: item.customer_or_vendor || "",
              debit:
                item.is_debit_or_credit === "debit"
                  ? item.amount.toString()
                  : "",
              credit:
                item.is_debit_or_credit === "credit"
                  ? item.amount.toString()
                  : "",
            }))
          : [];

        setLineItems(formLineItems);

        // Store original data
        setOriginalData({
          templateName: template.template_name,
          currencyId: template.currency_id || "",
          locationId: template.location_id ?? null,
          notes: template.notes || "",
          reportingMethod: template.type || "both",
          recurringFrequency: template.recurrence_frequency,
          recurringInterval: template.recurrence_interval || "",
          recurringStartDate: template.starts_on,
          recurringEndDate: template.ends_on || "",
          recurringNeverExpires: neverExpires,
          referenceNumberPrefix: template.reference_number_prefix || "",
          lineItems: formLineItems,
        });

        setIsPageLoading(false);
      } catch (error) {
        console.error("Error loading template:", error);
        setPageError("Failed to load recurring journal template");
        setIsPageLoading(false);
      }
    };

    loadTemplateData();
  }, [templateId]);

  // Fetch account types
  useEffect(() => {
    const loadAccountTypes = async () => {
      try {
        setAccountTypesLoading(true);
        const response = await journalsAPI.getAccountTypes();
        setAccountTypes(response.data.account_types);
      } catch (error) {
        console.error("Error fetching account types:", error);
      } finally {
        setAccountTypesLoading(false);
      }
    };

    loadAccountTypes();
  }, []);

  // Fetch currencies
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
      } catch (error) {
        console.error("Error fetching currencies:", error);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  // Fetch customers and vendors
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setCustomersLoading(true);
        const response = await getAllCustomers();
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
      } finally {
        setCustomersLoading(false);
      }
    };

    const loadVendors = async () => {
      try {
        setVendorsLoading(true);
        const response = await getAllVendors();
        setVendors(response.data || []);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setVendorsLoading(false);
      }
    };

    loadCustomers();
    loadVendors();
  }, []);

  // After account types are loaded, determine account types for existing line items
  useEffect(() => {
    if (accountTypes.length === 0 || lineItems.length === 0) return;
    if (lineItems.every((item) => item.account_type !== "")) return; // Already populated

    let isMounted = true;

    // For each account type, load the accounts and check if our account_id matches
    const determineAccountTypes = async () => {
      for (const accountType of accountTypes) {
        if (!isMounted) break;

        try {
          if (!accountsByType[accountType]) {
            const response =
              await journalsAPI.getGLMapsByAccountType(accountType);
            const accounts = response.data.gl_maps;

            if (!isMounted) break;

            setAccountsByType((prev) => ({
              ...prev,
              [accountType]: accounts,
            }));

            // Check if any line items match this account type and update them
            setLineItems((prevItems) => {
              let updated = false;
              const newItems = prevItems.map((item) => {
                if (!item.account_type && item.account_id) {
                  const matchingAccount = accounts.find(
                    (acc) => acc.account_id.toString() === item.account_id,
                  );
                  if (matchingAccount) {
                    updated = true;
                    return {
                      ...item,
                      account_type: accountType,
                    };
                  }
                }
                return item;
              });
              return updated ? newItems : prevItems;
            });
          }
        } catch (error) {
          console.error(
            "Error fetching accounts for type:",
            accountType,
            error,
          );
        }
      }
    };

    determineAccountTypes();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTypes]);

  // Update original data after all initial loading is complete
  useEffect(() => {
    // If already finalized, never update again
    if (originalDataFinalized) {
      return;
    }

    // Wait for all initial loading to complete
    if (
      accountTypesLoading ||
      customersLoading ||
      vendorsLoading ||
      currenciesLoading ||
      isPageLoading
    ) {
      return;
    }

    // Wait for line items to have account types populated
    if (
      lineItems.length === 0 ||
      !lineItems.every((item) => item.account_type !== "")
    ) {
      return;
    }

    // Only update once - check if original data has account types
    if (
      originalData &&
      originalData.lineItems.some((item) => !item.account_type)
    ) {
      // All loading complete and line items are fully populated - update original data
      setOriginalData((prevOriginalData) => ({
        ...prevOriginalData!,
        lineItems: JSON.parse(JSON.stringify(lineItems)), // Deep copy with account_type populated
      }));

      // Mark as finalized - this prevents any future updates to originalData
      setOriginalDataFinalized(true);
    }
  }, [
    accountTypesLoading,
    customersLoading,
    vendorsLoading,
    currenciesLoading,
    isPageLoading,
    lineItems,
    originalData,
    originalDataFinalized,
  ]);

  // Fetch accounts when account type is selected
  const handleAccountTypeChange = async (
    lineItemId: string,
    accountType: string,
  ) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? {
              ...item,
              account_type: accountType,
              account_id: "",
              account_name: "",
            }
          : item,
      ),
    );

    if (!accountsByType[accountType]) {
      try {
        const response = await journalsAPI.getGLMapsByAccountType(accountType);
        setAccountsByType((prev) => ({
          ...prev,
          [accountType]: response.data.gl_maps,
        }));
      } catch (error) {
        console.error("Error fetching accounts for type:", accountType, error);
      }
    }
  };

  // Handle account selection
  const handleAccountChange = (lineItemId: string, accountId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          const account = accountsByType[item.account_type]?.find(
            (a) => a.account_id.toString() === accountId,
          );
          return {
            ...item,
            account_id: accountId,
            account_name: account?.gl_name || "",
          };
        }
        return item;
      }),
    );
  };

  // Handle contact selection
  const handleContactChange = (lineItemId: string, contactId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          const customer = customers.find(
            (c) => c.zoho_customerid === contactId,
          );
          const vendor = vendors.find((v) => v.zoho_vendorid === contactId);

          return {
            ...item,
            contact_id: contactId,
            contact_name:
              customer?.customer_name || vendor?.vendor_legal_name || "",
            customer_or_vendor: customer
              ? ("customer" as const)
              : vendor
                ? ("vendor" as const)
                : "",
          };
        }
        return item;
      }),
    );
  };

  // Handle line item field change
  const handleLineItemChange = (
    lineItemId: string,
    field: keyof TemplateLineItemForm,
    value: string,
  ) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, [field]: value } : item,
      ),
    );
  };

  // Add new line item
  const handleAddRow = () => {
    const newId = (
      Math.max(...lineItems.map((item) => parseInt(item.id))) + 1
    ).toString();
    setLineItems((prev) => [
      ...prev,
      {
        id: newId,
        account_type: "",
        account_id: "",
        account_name: "",
        description: "",
        contact_id: "",
        contact_name: "",
        customer_or_vendor: "",
        debit: "",
        credit: "",
      },
    ]);
  };

  // Remove line item
  const handleRemoveRow = (lineItemId: string) => {
    if (lineItems.length > 2) {
      setLineItems((prev) => prev.filter((item) => item.id !== lineItemId));
    } else {
      showError("You must have at least 2 line items");
    }
  };

  // Calculate totals
  const getTotalDebits = (): number => {
    return lineItems.reduce((sum, item) => {
      const debit = parseFloat(item.debit) || 0;
      return sum + debit;
    }, 0);
  };

  const getTotalCredits = (): number => {
    return lineItems.reduce((sum, item) => {
      const credit = parseFloat(item.credit) || 0;
      return sum + credit;
    }, 0);
  };

  const getDifference = (): number => {
    return getTotalDebits() - getTotalCredits();
  };

  // Detect changes
  const detectChanges = (): TemplateChange[] => {
    if (!originalData) return [];

    const changes: TemplateChange[] = [];

    // Check template name
    if (templateName !== originalData.templateName) {
      changes.push({
        field: "template_name",
        label: "Template Name",
        oldValue: originalData.templateName,
        newValue: templateName,
      });
    }

    // Check currency
    if (currencyId !== originalData.currencyId) {
      const oldCurrency = currencies.find(
        (c) => c.currency_id === originalData.currencyId,
      );
      const newCurrency = currencies.find((c) => c.currency_id === currencyId);
      changes.push({
        field: "currencyId",
        label: "Currency",
        oldValue: oldCurrency
          ? `${oldCurrency.currency_code} – ${oldCurrency.currency_name}`
          : originalData.currencyId,
        newValue: newCurrency
          ? `${newCurrency.currency_code} – ${newCurrency.currency_name}`
          : currencyId,
      });
    }

    // Check notes
    if (notes !== originalData.notes) {
      changes.push({
        field: "notes",
        label: "Notes",
        oldValue: originalData.notes,
        newValue: notes,
      });
    }

    // Check reporting method
    if (reportingMethod !== originalData.reportingMethod) {
      const formatReportingMethod = (method: string) => {
        switch (method) {
          case "both":
            return "Accrual and Cash";
          case "cash":
            return "Cash Only";
          case "bank":
            return "Bank Only";
          default:
            return method;
        }
      };
      changes.push({
        field: "reportingMethod",
        label: "Reporting Method",
        oldValue: formatReportingMethod(originalData.reportingMethod),
        newValue: formatReportingMethod(reportingMethod),
      });
    }

    // Check recurring frequency
    if (recurringFrequency !== originalData.recurringFrequency) {
      changes.push({
        field: "recurringFrequency",
        label: "Recurrence Frequency",
        oldValue: originalData.recurringFrequency,
        newValue: recurringFrequency,
      });
    }

    // Check recurring interval
    if (recurringInterval !== originalData.recurringInterval) {
      changes.push({
        field: "recurringInterval",
        label: "Recurrence Interval",
        oldValue: originalData.recurringInterval
          ? `${originalData.recurringInterval} days`
          : "N/A",
        newValue: recurringInterval ? `${recurringInterval} days` : "N/A",
      });
    }

    // Check start date
    if (recurringStartDate !== originalData.recurringStartDate) {
      changes.push({
        field: "recurringStartDate",
        label: "Start Date",
        oldValue: new Date(originalData.recurringStartDate).toLocaleDateString(
          "en-US",
          {
            year: "numeric",
            month: "long",
            day: "numeric",
          },
        ),
        newValue: new Date(recurringStartDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      });
    }

    // Check end date
    if (recurringEndDate !== originalData.recurringEndDate) {
      changes.push({
        field: "recurringEndDate",
        label: "End Date",
        oldValue: originalData.recurringEndDate
          ? new Date(originalData.recurringEndDate).toLocaleDateString(
              "en-US",
              {
                year: "numeric",
                month: "long",
                day: "numeric",
              },
            )
          : "None",
        newValue: recurringEndDate
          ? new Date(recurringEndDate).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "None",
      });
    }

    // ZOUR-65 F4: moving the template to another GST registration changes every
    // journal it mints from here on, so it counts as a change and shows in the
    // confirm dialog. Compared on the RESOLVED location: NULL and the primary's
    // own id are the same registration.
    const resolveLocation = (id: string | null) =>
      activeLocations.find((candidate) => candidate.zoho_location_id === id) ??
      (id ? null : activeLocations.find((candidate) => candidate.is_primary) ?? null);

    const oldLocation = resolveLocation(originalData.locationId ?? null);
    const newLocation = resolveLocation(locationId ?? null);

    if (
      (oldLocation?.zoho_location_id ?? originalData.locationId ?? null) !==
      (newLocation?.zoho_location_id ?? locationId ?? null)
    ) {
      changes.push({
        field: "locationId",
        label: "Location",
        oldValue: oldLocation?.name ?? "Primary location",
        newValue: newLocation?.name ?? "Primary location",
      });
    }

    // Check never expires
    if (recurringNeverExpires !== originalData.recurringNeverExpires) {
      changes.push({
        field: "recurringNeverExpires",
        label: "Never Expires",
        oldValue: originalData.recurringNeverExpires ? "Yes" : "No",
        newValue: recurringNeverExpires ? "Yes" : "No",
      });
    }

    // Check line items
    lineItems.forEach((currentItem, index) => {
      const originalItem = originalData.lineItems.find(
        (item) => item.id === currentItem.id,
      );

      if (originalItem) {
        if (currentItem.account_type !== originalItem.account_type) {
          changes.push({
            field: `lineItem_${index}_accountType`,
            label: `Line Item ${index + 1} - Account Type`,
            oldValue: originalItem.account_type
              ? formatAccountType(originalItem.account_type)
              : "Not set",
            newValue: currentItem.account_type
              ? formatAccountType(currentItem.account_type)
              : "Not set",
          });
        }

        if (currentItem.account_id !== originalItem.account_id) {
          changes.push({
            field: `lineItem_${index}_account`,
            label: `Line Item ${index + 1} - Account`,
            oldValue: originalItem.account_name || "Not set",
            newValue: currentItem.account_name || "Not set",
          });
        }

        if (currentItem.description !== originalItem.description) {
          changes.push({
            field: `lineItem_${index}_description`,
            label: `Line Item ${index + 1} - Description`,
            oldValue: originalItem.description || "None",
            newValue: currentItem.description || "None",
          });
        }

        if (currentItem.contact_id !== originalItem.contact_id) {
          changes.push({
            field: `lineItem_${index}_contact`,
            label: `Line Item ${index + 1} - Contact`,
            oldValue: originalItem.contact_name || "None",
            newValue: currentItem.contact_name || "None",
          });
        }

        if (currentItem.debit !== originalItem.debit) {
          changes.push({
            field: `lineItem_${index}_debit`,
            label: `Line Item ${index + 1} - Debit`,
            oldValue: originalItem.debit
              ? `${currencySymbol}${parseFloat(originalItem.debit).toFixed(2)}`
              : "0.00",
            newValue: currentItem.debit
              ? `${currencySymbol}${parseFloat(currentItem.debit).toFixed(2)}`
              : "0.00",
          });
        }

        if (currentItem.credit !== originalItem.credit) {
          changes.push({
            field: `lineItem_${index}_credit`,
            label: `Line Item ${index + 1} - Credit`,
            oldValue: originalItem.credit
              ? `${currencySymbol}${parseFloat(originalItem.credit).toFixed(2)}`
              : "0.00",
            newValue: currentItem.credit
              ? `${currencySymbol}${parseFloat(currentItem.credit).toFixed(2)}`
              : "0.00",
          });
        }
      }
    });

    // Check if line items were added or removed
    if (lineItems.length !== originalData.lineItems.length) {
      changes.push({
        field: "lineItemCount",
        label: "Line Item Count",
        oldValue: `${originalData.lineItems.length} items`,
        newValue: `${lineItems.length} items`,
      });
    }

    return changes;
  };

  // Check if there are any changes
  const hasChanges = (): boolean => {
    return detectChanges().length > 0;
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!templateName || templateName.trim() === "")
      newErrors.templateName = "Template name is required";
    if (!notes || notes.trim() === "") newErrors.notes = "Notes are required";
    if (!recurringStartDate)
      newErrors.recurringStartDate = "Start date is required";
    if (!recurringFrequency)
      newErrors.recurringFrequency = "Frequency is required";
    if (recurringFrequency === "every_n_days" && !recurringInterval)
      newErrors.recurringInterval = "Number of days is required";
    if (!recurringNeverExpires && !recurringEndDate)
      newErrors.recurringEndDate =
        "End date is required or select 'Never expires'";

    if (notes.length > 500)
      newErrors.notes = "Notes cannot exceed 500 characters";

    // Validate line items
    lineItems.forEach((item, index) => {
      if (!item.account_id)
        newErrors[`lineItem_${index}_account`] = "Account is required";

      if (isContactRequired(item.account_name) && !item.contact_id) {
        newErrors[`lineItem_${index}_contact`] =
          "Contact is required for this account type";
      }

      const debit = parseFloat(item.debit) || 0;
      const credit = parseFloat(item.credit) || 0;

      if (debit === 0 && credit === 0) {
        newErrors[`lineItem_${index}_amount`] =
          "Either debit or credit must be specified";
      }

      if (debit > 0 && credit > 0) {
        newErrors[`lineItem_${index}_amount`] =
          "Cannot have both debit and credit in the same line";
      }
    });

    // Check if debits equal credits
    const difference = getDifference();
    if (Math.abs(difference) > 0.01) {
      newErrors.balance = "Total debits must equal total credits";
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle update template
  const handleUpdateTemplate = async (skipModal: boolean = false) => {
    if (!hasChanges() && !skipModal) {
      showError("No changes detected");
      return;
    }

    if (!skipModal) {
      setConfirmModalOpen(true);
      return;
    }

    if (!validateForm()) {
      // ErrorBanner will display validation errors and auto-scroll
      return;
    }

    if (!templateId) {
      setFieldError("templateId", "Template ID is missing");
      showError("Template ID is missing");
      return;
    }

    try {
      setIsLoading(true);

      // Prepare line items for API
      const apiLineItems: CreateJournalLineItem[] = lineItems.map((item) => {
        const debit = parseFloat(item.debit) || 0;
        const credit = parseFloat(item.credit) || 0;

        return {
          account_id: item.account_id,
          is_debit_or_credit: debit > 0 ? "debit" : "credit",
          amount: debit > 0 ? debit : credit,
          description: item.description,
          contact_id: item.contact_id || undefined,
          customer_or_vendor: item.customer_or_vendor || undefined,
        };
      });

      // Prepare template data
      const templateData: UpdateTemplateRequest = {
        template_name: templateName,
        notes,
        type: reportingMethod,
        recurrence_frequency: recurringFrequency,
        recurrence_interval:
          recurringFrequency === "every_n_days"
            ? (recurringInterval as number)
            : null,
        starts_on: recurringStartDate,
        ends_on: recurringNeverExpires ? null : recurringEndDate,
        never_expires: recurringNeverExpires,
        // ZOUR-65 F4: changing this moves every FUTURE journal this template
        // mints to another registration; the ones already generated keep theirs.
        location_id: locationId,
        line_items: apiLineItems,
      };

      console.log("Updating template:", templateData);
      await journalsAPI.updateRecurringTemplate(templateId, templateData);

      showSuccess("Recurring journal template updated successfully");
      navigate(`/journal/recurring/${templateId}`);
    } catch (error: any) {
      console.error("Error updating template:", error);
      // Use centralized error parsing and display
      setApiErrors(error);
    } finally {
      setIsLoading(false);
      setConfirmModalOpen(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };

  // Check if all initial data is loaded
  const isAllDataLoaded =
    !accountTypesLoading &&
    !customersLoading &&
    !vendorsLoading &&
    !currenciesLoading &&
    !isPageLoading;

  // If page is loading
  if (isPageLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <Loader />
        </div>
      </DefaultPageLayout>
    );
  }

  // If there's a page error
  if (pageError) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <IconWithBackground
              variant="error"
              icon={<FeatherAlertTriangle />}
            />
            <span className="text-heading-2 font-heading-2 text-default-font">
              Failed to Load Template
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              {pageError}
            </span>
            <Button onClick={() => navigate(-1)}>Back</Button>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full items-center gap-3 border-b border-solid border-neutral-border px-8 py-4">
          <IconButton icon={<FeatherArrowLeft />} onClick={handleCancel} />
          <span className="text-body font-body text-default-font">
            Back to Recurring Journal Details
          </span>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 px-8 py-8 pb-16 overflow-auto">
          <div className="flex w-full flex-col items-start gap-6 px-1 py-1">
            <span className="text-heading-1 font-heading-1 text-default-font">
              Edit Recurring Journal Template
            </span>
          </div>

          {/* Display validation errors with new ErrorBanner component */}
          <div className="w-full pb-2">
            <ErrorBanner
              errors={errors}
              title="Please fix the following errors:"
              autoScrollOnMount={true}
            />
          </div>

          <div className="flex w-full flex-col items-start gap-6">
            <div className="w-full items-start gap-6 grid grid-cols-2">
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Template Name
                  </span>
                  <span className="text-body-bold font-body-bold text-error-600">
                    *
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={errors.templateName}
                  error={!!errors.templateName}
                  disabled={!isAllDataLoaded}
                >
                  <TextField.Input
                    placeholder="Enter template name"
                    value={templateName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setTemplateName(event.target.value);
                      clearFieldError("templateName");
                    }}
                    disabled={!isAllDataLoaded}
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Currency
                </span>
                <SearchableDropdown
                  items={currencies}
                  loading={!isAllDataLoaded}
                  trigger={
                    <Button
                      variant="neutral-secondary"
                      className="w-full justify-between"
                      iconRight={<FeatherChevronDown />}
                      disabled={!isAllDataLoaded}
                    >
                      {!isAllDataLoaded
                        ? "Loading..."
                        : currencyId
                          ? currencies.find((c) => c.currency_id === currencyId)
                            ? `${
                                currencies.find(
                                  (c) => c.currency_id === currencyId,
                                )?.currency_code
                              } – ${
                                currencies.find(
                                  (c) => c.currency_id === currencyId,
                                )?.currency_name
                              }`
                            : "Select currency"
                          : "Select currency"}
                    </Button>
                  }
                  getSearchableText={(currency) =>
                    `${currency.currency_code} – ${currency.currency_name}`
                  }
                  renderItem={(currency) =>
                    `${currency.currency_code} – ${currency.currency_name}`
                  }
                  getItemKey={(currency) => currency.currency_id}
                  onSelect={(currency) => {
                    setCurrencyId(currency.currency_id);
                    setCurrencySymbol(currency.currency_symbol);
                  }}
                  selectedKey={currencyId}
                  searchPlaceholder="Search currencies..."
                  emptyMessage="No currencies available"
                  noResultsMessage="No currencies match your search"
                />
              </div>
              {/* ZOUR-65 F4: the registration every journal this template
                  generates is filed under. Hidden on a single-registration
                  company. */}
              {hasMultipleLocations && (
                <div className="flex flex-col items-start gap-2">
                  <LocationPicker
                    locations={activeLocations}
                    value={locationId}
                    onChange={(picked) => setLocationId(picked)}
                    disabled={!isAllDataLoaded}
                  />
                </div>
              )}
              <div className="flex flex-col items-start gap-2 col-span-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Notes
                  </span>
                  <span className="text-body-bold font-body-bold text-error-600">
                    *
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={
                    errors.notes || `Max. 500 characters · ${notes.length}/500`
                  }
                  error={!!errors.notes}
                  disabled={!isAllDataLoaded}
                >
                  <TextField.Input
                    placeholder="Enter notes"
                    value={notes}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setNotes(event.target.value);
                      clearFieldError("notes");
                    }}
                    maxLength={500}
                    disabled={!isAllDataLoaded}
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2 col-span-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Reporting Method
                </span>
                <div className="flex gap-4">
                  <label
                    className={`flex items-center gap-2 p-3 border border-gray-200 rounded-md ${
                      !isAllDataLoaded
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportingMethod"
                      value="both"
                      checked={reportingMethod === "both"}
                      onChange={(e) => setReportingMethod(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                      disabled={!isAllDataLoaded}
                    />
                    <span className="text-body font-body text-default-font">
                      Accrual and Cash
                    </span>
                  </label>
                  <label
                    className={`flex items-center gap-2 p-3 border border-gray-200 rounded-md ${
                      !isAllDataLoaded
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportingMethod"
                      value="cash"
                      checked={reportingMethod === "cash"}
                      onChange={(e) => setReportingMethod(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                      disabled={!isAllDataLoaded}
                    />
                    <span className="text-body font-body text-default-font">
                      Cash Only
                    </span>
                  </label>
                </div>
              </div>

              {/* Recurring Journal Section - Always shown, YES/NO disabled */}
              <div className="flex flex-col items-start gap-2 col-span-2">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Recurring Journal
                </span>
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-solid border-blue-200">
                  <span className="text-body font-body text-blue-700">
                    This is a recurring journal template. You can modify the
                    recurrence settings below.
                  </span>
                </div>
              </div>

              {/* Recurring Fields */}
              <div className="flex flex-col items-start gap-4 border-l-2 border-solid border-neutral-200 pl-4 col-span-2">
                <div className="w-full items-start gap-6 grid grid-cols-2">
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Repeat Every
                      </span>
                      <span className="text-body-bold font-body-bold text-error-600">
                        *
                      </span>
                    </div>
                    <Select
                      label=""
                      placeholder="Select frequency"
                      value={recurringFrequency}
                      onValueChange={(value: string) => {
                        setRecurringFrequency(value);
                        clearFieldError("recurringFrequency");
                      }}
                      error={!!errors.recurringFrequency}
                      helpText={errors.recurringFrequency}
                      disabled={!isAllDataLoaded}
                    >
                      <Select.Item value="daily">Daily</Select.Item>
                      <Select.Item value="start_of_month">
                        Start of Month
                      </Select.Item>
                      <Select.Item value="end_of_month">
                        End of Month
                      </Select.Item>
                      <Select.Item value="every_n_days">
                        Every N Days
                      </Select.Item>
                    </Select>
                  </div>
                  {recurringFrequency === "every_n_days" && (
                    <div className="flex flex-col items-start gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Number of Days
                        </span>
                        <span className="text-body-bold font-body-bold text-error-600">
                          *
                        </span>
                      </div>
                      <TextField
                        className="h-auto w-full flex-none"
                        label=""
                        helpText={errors.recurringInterval}
                        error={!!errors.recurringInterval}
                        disabled={!isAllDataLoaded}
                      >
                        <TextField.Input
                          type="number"
                          placeholder="Enter number of days"
                          value={recurringInterval.toString()}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setRecurringInterval(
                              event.target.value
                                ? parseInt(event.target.value)
                                : "",
                            );
                            clearFieldError("recurringInterval");
                          }}
                          min="1"
                          disabled={!isAllDataLoaded}
                        />
                      </TextField>
                    </div>
                  )}
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Starts On
                      </span>
                      <span className="text-body-bold font-body-bold text-error-600">
                        *
                      </span>
                    </div>
                    <TextField
                      className="h-auto w-full flex-none"
                      label=""
                      helpText={errors.recurringStartDate}
                      error={!!errors.recurringStartDate}
                      icon={<FeatherCalendar />}
                      disabled={!isAllDataLoaded}
                    >
                      <TextField.Input
                        type="date"
                        placeholder="Select start date"
                        value={recurringStartDate}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setRecurringStartDate(event.target.value);
                          clearFieldError("recurringStartDate");
                        }}
                        disabled={!isAllDataLoaded}
                        min={new Date().toISOString().split("T")[0]}
                      />
                    </TextField>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Ends On
                    </span>
                    <TextField
                      className="h-auto w-full flex-none"
                      label=""
                      helpText=""
                      icon={<FeatherCalendar />}
                      disabled={recurringNeverExpires || !isAllDataLoaded}
                    >
                      <TextField.Input
                        type="date"
                        placeholder="Select end date"
                        value={recurringEndDate}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setRecurringEndDate(event.target.value);
                          clearFieldError("recurringEndDate");
                        }}
                        disabled={recurringNeverExpires || !isAllDataLoaded}
                      />
                    </TextField>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 flex-none rounded-md border border-solid border-neutral-300"
                    checked={recurringNeverExpires}
                    onChange={(e) => setRecurringNeverExpires(e.target.checked)}
                    disabled={!isAllDataLoaded}
                  />
                  <span
                    className={`text-body font-body ${
                      !isAllDataLoaded
                        ? "text-neutral-400"
                        : "text-default-font"
                    }`}
                  >
                    Never expires
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col items-start gap-6">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Line Items
            </span>
            <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border overflow-x-auto overflow-y-visible">
              <div className="flex items-center gap-4 border-b border-solid border-neutral-border bg-neutral-50 px-4 py-3 min-w-full">
                <div className="flex w-8 flex-none items-center">
                  <span className="text-body-bold font-body-bold text-default-font">
                    #
                  </span>
                </div>
                <div className="flex w-48 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    Account Type
                  </span>
                </div>
                <div className="flex w-48 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    Account
                  </span>
                </div>
                <div className="flex w-64 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    Description
                  </span>
                </div>
                <div className="flex w-48 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    Contact ({currencySymbol})
                  </span>
                </div>
                <div className="flex w-32 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    Debits
                  </span>
                </div>
                <div className="flex w-32 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    Credits
                  </span>
                </div>
                <div className="flex w-20 flex-none items-start" />
              </div>

              {lineItems.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-center gap-4 border-b border-solid border-neutral-100 bg-white px-4 py-3 min-w-full overflow-visible"
                >
                  <div className="flex w-8 flex-none items-center">
                    <span className="text-body-bold font-body-bold text-subtext-color">
                      {index + 1}
                    </span>
                  </div>
                  <div className="w-48 flex-none flex items-center overflow-hidden">
                    <Select
                      label=""
                      className="w-full [&_.whitespace-nowrap]:truncate"
                      placeholder={
                        !isAllDataLoaded ? "Loading..." : "Select type"
                      }
                      value={item.account_type}
                      onValueChange={(value) =>
                        handleAccountTypeChange(item.id, value)
                      }
                      disabled={!isAllDataLoaded}
                    >
                      {accountTypes.map((type) => (
                        <Select.Item key={type} value={type}>
                          {formatAccountType(type)}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <div className="w-48 flex-none flex items-center overflow-hidden">
                    <Select
                      label=""
                      className="w-full [&_.whitespace-nowrap]:truncate"
                      placeholder="Select account"
                      value={item.account_id}
                      onValueChange={(value) =>
                        handleAccountChange(item.id, value)
                      }
                      disabled={!item.account_type || !isAllDataLoaded}
                    >
                      {accountsByType[item.account_type]?.map((account) => (
                        <Select.Item
                          key={account.account_id}
                          value={account.account_id.toString()}
                        >
                          {account.gl_name}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <div className="flex w-64 flex-none items-center">
                    <TextField label="" helpText="">
                      <TextField.Input
                        placeholder="Enter description"
                        value={item.description}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          handleLineItemChange(
                            item.id,
                            "description",
                            event.target.value,
                          )
                        }
                        disabled={!item.account_id || !isAllDataLoaded}
                      />
                    </TextField>
                  </div>
                  <div className="w-48 flex-none flex items-center overflow-hidden">
                    <SearchableDropdown
                      items={buildGroupedContacts(item.account_name)}
                      loading={false}
                      trigger={
                        <Button
                          variant="neutral-secondary"
                          className="w-full justify-between"
                          iconRight={<FeatherChevronDown />}
                          disabled={!item.account_id || !isAllDataLoaded}
                        >
                          <span className="truncate text-left">
                            {getContactDisplayName(item.contact_id, item)}
                          </span>
                        </Button>
                      }
                      getSearchableText={(contact) =>
                        contact.customer_name || contact.vendor_legal_name
                      }
                      renderItem={(contact) =>
                        contact.customer_name || contact.vendor_legal_name
                      }
                      getItemKey={(contact) =>
                        contact.zoho_customerid || contact.zoho_vendorid
                      }
                      onSelect={(contact) => {
                        const contactId =
                          contact.zoho_customerid || contact.zoho_vendorid;
                        handleContactChange(item.id, contactId);
                      }}
                      selectedKey={item.contact_id}
                      searchPlaceholder="Search contacts..."
                      emptyMessage="No contacts available"
                      noResultsMessage="No contacts match your search"
                    />
                  </div>
                  <div className="flex w-32 flex-none items-center">
                    <TextField label="" helpText="">
                      <TextField.Input
                        type="number"
                        placeholder="0.00"
                        value={item.debit}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          handleLineItemChange(
                            item.id,
                            "debit",
                            event.target.value,
                          );
                          if (
                            event.target.value &&
                            parseFloat(event.target.value) > 0
                          ) {
                            handleLineItemChange(item.id, "credit", "");
                          }
                        }}
                        step="0.01"
                        min="0"
                        disabled={!item.account_id || !isAllDataLoaded}
                      />
                    </TextField>
                  </div>
                  <div className="flex w-32 flex-none items-center">
                    <TextField label="" helpText="">
                      <TextField.Input
                        type="number"
                        placeholder="0.00"
                        value={item.credit}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          handleLineItemChange(
                            item.id,
                            "credit",
                            event.target.value,
                          );
                          if (
                            event.target.value &&
                            parseFloat(event.target.value) > 0
                          ) {
                            handleLineItemChange(item.id, "debit", "");
                          }
                        }}
                        step="0.01"
                        min="0"
                        disabled={!item.account_id || !isAllDataLoaded}
                      />
                    </TextField>
                  </div>
                  <div className="flex w-20 flex-none items-center gap-1">
                    <IconButton
                      size="small"
                      icon={<FeatherX />}
                      onClick={() => handleRemoveRow(item.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
            <Button
              className="h-auto w-auto flex-none"
              variant="neutral-tertiary"
              icon={<FeatherPlus />}
              onClick={handleAddRow}
              disabled={!isAllDataLoaded}
            >
              Add New Row
            </Button>
          </div>

          {/* Error message for balance */}
          {errors.balance && (
            <div className="flex w-full items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-solid border-error-200">
              <FeatherX className="h-4 w-4 text-error-600" />
              <span className="text-body font-body text-error-700">
                {errors.balance}
              </span>
            </div>
          )}

          <div className="flex w-full items-start justify-end">
            <div className="flex w-96 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
              <span className="text-body-bold font-body-bold text-default-font">
                Summary
              </span>
              <div className="flex w-full flex-col items-start gap-3">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Debits
                  </span>
                  <span className="text-body font-body text-default-font">
                    {currencySymbol} {getTotalDebits().toFixed(2)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Credits
                  </span>
                  <span className="text-body font-body text-default-font">
                    {currencySymbol} {getTotalCredits().toFixed(2)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Total ({currencySymbol})
                  </span>
                  <div className="flex items-center gap-4">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getTotalDebits().toFixed(2)}
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getTotalCredits().toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Difference
                  </span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      Math.abs(getDifference()) < 0.01
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    {currencySymbol} {Math.abs(getDifference()).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Attachments - Commented out for now */}
          {/* <div className="flex w-full flex-col items-start gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Attachments
            </span>
            <div className="flex items-center gap-3">
              <input
                type="file"
                id="file-upload-edit"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="*"
              />
              <label htmlFor="file-upload-edit">
                <Button
                  className="h-auto w-auto flex-none"
                  variant="neutral-secondary"
                  iconRight={<FeatherPlus />}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    document.getElementById("file-upload-edit")?.click();
                  }}
                >
                  Upload File
                </Button>
              </label>
              <span className="text-caption font-caption text-subtext-color">
                You can upload a maximum of 5 files, 10MB each
              </span>
            </div>
          </div> */}

          <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border pt-6">
            <Button variant="neutral-tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => handleUpdateTemplate(false)}
              disabled={!isAllDataLoaded || isLoading || !hasChanges()}
            >
              {isLoading ? "Updating..." : "Update Recurring Journal"}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirm Edit Modal */}
      <ConfirmEditRecurringJournalModal
        open={confirmModalOpen}
        onOpenChange={setConfirmModalOpen}
        changes={detectChanges()}
        onConfirm={() => handleUpdateTemplate(true)}
        loading={isLoading}
      />
    </DefaultPageLayout>
  );
};

export default EditRecurringJournalPage;
